import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useLanguage } from './LanguageContext';
import { useAuth } from './AuthContext';
import { useBrand } from './BrandContext';
import Settings from './pages/Settings';
import Channels from './pages/Channels';
import Executive from './pages/Executive';
import Leads from './pages/Leads';
import TodayTasks from './pages/TodayTasks';
import LeadAnalytics from './pages/LeadAnalytics';
import MemberAnalytics from './pages/MemberAnalytics';
import Login from './pages/Login';
import UnitsDeals from './pages/UnitsDeals';
import DealRequests from './pages/DealRequests';
import BrokerPortal from './pages/BrokerPortal';
import Assistant from './pages/Assistant';
import BrokerEmployees from './pages/BrokerEmployees';
import BrokerAnalytics from './pages/BrokerAnalytics';
import BrokerMonitor from './pages/BrokerMonitor';
import AdminCommissions from './pages/AdminCommissions';
import StaleLeads from './pages/StaleLeads';
import CampaignComposer from './pages/CampaignComposer';
import Maintenance from './pages/Maintenance';
import Tasks from './pages/Tasks';
import ClosureTasks from './pages/ClosureTasks';
import BrokerCommissions from './pages/BrokerCommissions';
import ProjectMedia from './pages/ProjectMedia';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { isDark, toggle: toggleTheme } = useTheme();
  const { lang, isRTL, toggleLang, t } = useLanguage();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const brand = useBrand();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>&#9671;</div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  const isBroker = user?.role === 'broker';
  const isBrokerOwner = isBroker && !!user?.broker_owner;
  // Broker may see financials (deals + commissions) if owner, or granted broker.viewFinancials.
  const canBrokerFinancials = isBroker && !!(user?.broker_owner || user?.permissions?.['broker.viewFinancials']);
  const isSales = user?.role === 'sales';

  // Page-level access for nav + routes. With no per-user override this returns
  // EXACTLY today's nav visibility (sales: today+leads; everyone else: all the
  // non-sales pages). An explicit override on the user's `permissions` object
  // widens or narrows that. Brokers are never governed here. Admins fall back to
  // role defaults (they have no overrides) so their nav is unchanged.
  const pageAllowed = (key) => {
    if (isBroker) return false;
    const perms = user?.permissions;
    if (perms && Object.prototype.hasOwnProperty.call(perms, key)) return perms[key] === true;
    if (key === 'page.today') return isSales || user?.role === 'admin' || user?.role === 'manager';
    if (key === 'page.leads') return true;
    // Deal-requests page: sales (submit) + admin/manager (review). Not viewers.
    if (key === 'page.deal_requests') return isSales || user?.role === 'admin' || user?.role === 'manager';
    // AI assistant: admin/manager (director) only — not sales/viewer.
    if (key === 'page.assistant') return user?.role === 'admin' || user?.role === 'manager';
    // Maintenance: open to all internal staff (any non-broker) by default.
    if (key === 'page.maintenance') return true;
    // Tasks: shared team board — every internal member by default.
    if (key === 'page.tasks') return true;
    // My closure tasks: every internal member (they only ever see their own steps).
    if (key === 'page.closure_tasks') return true;
    // Director/admin oversight pages: admin/manager by default (an explicit
    // per-user override above can still grant them to a viewer/sales).
    if (key === 'page.brokers' || key === 'page.commissions' || key === 'page.stale_leads' || key === 'page.campaigns' || key === 'page.project_media') {
      return user?.role === 'admin' || user?.role === 'manager';
    }
    return !isSales;
  };
  // Action permissions (e.g. project_media.manage): per-user override wins, else
  // admin/manager by default. Brokers are never governed here.
  const actionAllowed = (key) => {
    if (isBroker) return false;
    const perms = user?.permissions;
    if (perms && Object.prototype.hasOwnProperty.call(perms, key)) return perms[key] === true;
    return user?.role === 'admin' || user?.role === 'manager';
  };

  const accessDenied = (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
      {lang === 'ar' ? '\u0644\u0627 \u062A\u0645\u0644\u0643 \u0635\u0644\u0627\u062D\u064A\u0629 \u0644\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0647\u0630\u0647 \u0627\u0644\u0635\u0641\u062D\u0629' : 'You do not have access to this page'}
    </div>
  );

  const getPageTitle = () => {
    if (location.pathname.startsWith('/units-deals')) {
      const sub = location.pathname.replace('/units-deals', '').replace(/^\//, '');
      const parent = t('pageUnitsDeals');
      if (sub === 'deals') return `${parent} \u00B7 ${lang === 'ar' ? '\u0627\u0644\u0635\u0641\u0642\u0627\u062A' : 'Deals'}`;
      if (sub === 'closure') return `${parent} \u00B7 ${lang === 'ar' ? '\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0635\u0641\u0642\u0629' : 'Closure'}`;
      if (sub === 'reports') return `${parent} \u00B7 ${lang === 'ar' ? '\u0627\u0644\u062A\u0642\u0627\u0631\u064A\u0631' : 'Reports'}`;
      if (sub === 'settings') return `${parent} \u00B7 ${lang === 'ar' ? '\u0627\u0644\u0625\u0639\u062F\u0627\u062F\u0627\u062A' : 'Settings'}`;
      return parent;
    }
    switch (location.pathname) {
      case '/portal': return lang === 'ar' ? 'بوابة الوسطاء' : 'Broker Portal';
      case '/executive': return t('pageExecutive');
      case '/today': return t('crmTodayTasks');
      case '/leads': return t('pageLeads');
      case '/analytics': return t('navAnalytics');
      case '/assistant': return t('assistantTitle');
      case '/broker-analytics': return t('navAnalytics');
      case '/brokers': return lang === 'ar' ? 'مراقبة الوسطاء' : 'Broker Monitoring';
      case '/commissions': return lang === 'ar' ? 'عمولات الوسطاء' : 'Broker Commissions';
      case '/stale-leads': return lang === 'ar' ? 'العملاء الراكدون' : 'Stale Leads';
      case '/maintenance': return lang === 'ar' ? 'الصيانة' : 'Maintenance';
      case '/tasks': return lang === 'ar' ? 'المهام' : 'Tasks';
      case '/closure-tasks': return lang === 'ar' ? 'مهام الإغلاق' : 'Closure Tasks';
      case '/broker-commissions': return lang === 'ar' ? 'العمولات' : 'Commissions';
      case '/broker-employees': return lang === 'ar' ? 'الموظفون' : 'Employees';
      case '/channels': return t('pageChannels');
      case '/settings': return t('pageSettings');
      default: return isBroker ? (lang === 'ar' ? 'بوابة الوسطاء' : 'Broker Portal') : t('pageExecutive');
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className={`app-layout ${isRTL ? 'rtl' : 'ltr'}`}>
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={closeSidebar}
      />

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} role="navigation" aria-label="Main navigation">
        <div className="sidebar-logo">
          {brand.brandLogo && <img src={brand.brandLogo} alt={brand.brandName} style={{ height: 32, filter: 'brightness(1.1)' }} onError={(e) => { e.target.style.display = 'none'; }} />}
        </div>
        <nav className="sidebar-nav">
          {isBroker && (
            <NavLink to="/portal" end className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: 'var(--accent)' }}>&#127968;</span>
              <span className="nav-label">{lang === 'ar' ? 'بوابة الوسطاء' : 'Broker Portal'}</span>
            </NavLink>
          )}
          {isBroker && (
            <>
              <NavLink to="/today" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
                <span className="nav-icon" style={{ color: '#f59e0b' }}>&#9200;</span>
                <span className="nav-label">{t('navTodayTasks')}</span>
              </NavLink>
              <NavLink to="/leads" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
                <span className="nav-icon" style={{ color: '#22c55e' }}>&#9824;</span>
                <span className="nav-label">{t('navLeads')}</span>
              </NavLink>
              <NavLink to="/project-media" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
                <span className="nav-icon" style={{ color: '#ec4899' }}>&#127916;</span>
                <span className="nav-label">{lang === 'ar' ? 'وسائط المشاريع' : 'Project media'}</span>
              </NavLink>
              {canBrokerFinancials && (
                <NavLink to="/broker-commissions" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
                  <span className="nav-icon" style={{ color: '#22c55e' }}>&#128181;</span>
                  <span className="nav-label">{lang === 'ar' ? 'العمولات' : 'Commissions'}</span>
                </NavLink>
              )}
              {isBrokerOwner && (
                <>
                  <NavLink to="/broker-analytics" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
                    <span className="nav-icon" style={{ color: '#8b5cf6' }}>&#9733;</span>
                    <span className="nav-label">{t('navAnalytics')}</span>
                  </NavLink>
                  <NavLink to="/broker-employees" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
                    <span className="nav-icon" style={{ color: '#06b6d4' }}>&#128100;</span>
                    <span className="nav-label">{lang === 'ar' ? 'الموظفون' : 'Employees'}</span>
                  </NavLink>
                </>
              )}
            </>
          )}
          {/* ── التسويق والإعلانات — plan, publish, measure ads ── */}
          {!isBroker && ((pageAllowed('page.executive') && brand.executiveEnabled !== false) || (pageAllowed('page.channels') && brand.channelsEnabled !== false) || (pageAllowed('page.campaigns') && brand.campaignsEnabled !== false)) && (
            <div className="nav-section-heading">{lang === 'ar' ? '📣 التسويق والإعلانات' : '📣 Marketing & Ads'}</div>
          )}
          {pageAllowed('page.executive') && brand.executiveEnabled !== false && (
            <NavLink to="/executive" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: 'var(--accent)' }}>&#9670;</span>
              <span className="nav-label">{t('navExecutive')}</span>
            </NavLink>
          )}
          {pageAllowed('page.channels') && brand.channelsEnabled !== false && (
            <NavLink to="/channels" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon">&#9776;</span>
              <span className="nav-label">{t('navChannels')}</span>
            </NavLink>
          )}
          {pageAllowed('page.campaigns') && brand.campaignsEnabled !== false && (
            <NavLink to="/campaign-composer" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#f43f5e' }}>&#128226;</span>
              <span className="nav-label">{lang === 'ar' ? 'نشر حملة' : 'Publish campaign'}</span>
            </NavLink>
          )}

          {/* ── العملاء والمبيعات — the sales pipeline ── */}
          {!isBroker && (pageAllowed('page.today') || pageAllowed('page.leads') || pageAllowed('page.tasks') || pageAllowed('page.analytics') || pageAllowed('page.assistant')) && (
            <div className="nav-section-heading">{lang === 'ar' ? '👥 العملاء والمبيعات' : '👥 CRM & Sales'}</div>
          )}
          {pageAllowed('page.today') && (
            <NavLink to="/today" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#f59e0b' }}>&#9200;</span>
              <span className="nav-label">{t('navTodayTasks')}</span>
            </NavLink>
          )}
          {pageAllowed('page.leads') && (
            <NavLink to="/leads" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#22c55e' }}>&#9824;</span>
              <span className="nav-label">{t('navLeads')}</span>
            </NavLink>
          )}
          {pageAllowed('page.tasks') && (
            <NavLink to="/tasks" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#a855f7' }}>&#128221;</span>
              <span className="nav-label">{lang === 'ar' ? 'المهام' : 'Tasks'}</span>
            </NavLink>
          )}
          {pageAllowed('page.closure_tasks') && (
            <NavLink to="/closure-tasks" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#f5a623' }}>&#9733;</span>
              <span className="nav-label">{lang === 'ar' ? 'مهام الإغلاق' : 'Closure Tasks'}</span>
            </NavLink>
          )}
          {pageAllowed('page.analytics') && (
            <NavLink to="/analytics" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#8b5cf6' }}>&#9733;</span>
              <span className="nav-label">{t('navAnalytics')}</span>
            </NavLink>
          )}
          {pageAllowed('page.assistant') && (
            <NavLink to="/assistant" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#0ea5e9' }}>&#128172;</span>
              <span className="nav-label">{t('navAssistant')}</span>
            </NavLink>
          )}

          {/* ── العقارات والمشاريع — property / units / deals ── */}
          {!isBroker && (pageAllowed('page.units_deals') || pageAllowed('page.maintenance') || pageAllowed('page.deal_requests')) && (
            <div className="nav-section-heading">{lang === 'ar' ? '🏢 العقارات والمشاريع' : '🏢 Real estate'}</div>
          )}
          {pageAllowed('page.units_deals') && (
            <>
              <NavLink
                to="/units-deals"
                end
                className={({ isActive }) =>
                  `nav-link ${isActive || location.pathname.startsWith('/units-deals') ? 'active' : ''}`
                }
                onClick={closeSidebar}
              >
                <span className="nav-icon" style={{ color: '#c9a84c' }}>&#127968;</span>
                <span className="nav-label">{lang === 'ar' ? 'إدارة المشاريع' : 'Project management'}</span>
              </NavLink>
              {(location.pathname.startsWith('/units-deals') || location.pathname === '/project-media') && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    [isRTL ? 'marginRight' : 'marginLeft']: 22,
                    [isRTL ? 'paddingRight' : 'paddingLeft']: 10,
                    [isRTL ? 'borderRight' : 'borderLeft']: '1.5px solid var(--border)',
                    marginTop: 2,
                    marginBottom: 4,
                  }}
                >
                  <NavLink to="/units-deals" end className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar} style={{ padding: '7px 10px', fontSize: '0.82rem' }}>
                    <span className="nav-label">{lang === 'ar' ? '\u0627\u0644\u0648\u062D\u062F\u0627\u062A' : 'Units'}</span>
                  </NavLink>
                  <NavLink to="/units-deals/deals" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar} style={{ padding: '7px 10px', fontSize: '0.82rem' }}>
                    <span className="nav-label">{lang === 'ar' ? '\u0627\u0644\u0635\u0641\u0642\u0627\u062A' : 'Deals'}</span>
                  </NavLink>
                  <NavLink to="/units-deals/closure" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar} style={{ padding: '7px 10px', fontSize: '0.82rem' }}>
                    <span className="nav-label">{lang === 'ar' ? '\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0635\u0641\u0642\u0629' : 'Closure'}</span>
                  </NavLink>
                  <NavLink to="/units-deals/reports" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar} style={{ padding: '7px 10px', fontSize: '0.82rem' }}>
                    <span className="nav-label">{lang === 'ar' ? '\u0627\u0644\u062A\u0642\u0627\u0631\u064A\u0631' : 'Reports'}</span>
                  </NavLink>
                  <NavLink to="/units-deals/settings" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar} style={{ padding: '7px 10px', fontSize: '0.82rem' }}>
                    <span className="nav-label">{lang === 'ar' ? '\u0627\u0644\u0625\u0639\u062F\u0627\u062F\u0627\u062A' : 'Settings'}</span>
                  </NavLink>
                  {pageAllowed('page.project_media') && (
                    <NavLink to="/project-media" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar} style={{ padding: '7px 10px', fontSize: '0.82rem' }}>
                      <span className="nav-label">{lang === 'ar' ? '\u0648\u0633\u0627\u0626\u0637 \u0627\u0644\u0645\u0634\u0627\u0631\u064A\u0639' : 'Project media'}</span>
                    </NavLink>
                  )}
                </div>
              )}
            </>
          )}
          {pageAllowed('page.maintenance') && (
            <NavLink to="/maintenance" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#f97316' }}>&#128295;</span>
              <span className="nav-label">{lang === 'ar' ? 'الصيانة' : 'Maintenance'}</span>
            </NavLink>
          )}
          {pageAllowed('page.deal_requests') && (
            <NavLink to="/deal-requests" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#06b6d4' }}>&#128203;</span>
              <span className="nav-label">{lang === 'ar' ? 'طلبات الصفقات' : 'Deal Requests'}</span>
            </NavLink>
          )}

          {/* ── الوسطاء — partners + their payouts ── */}
          {!isBroker && (pageAllowed('page.brokers') || pageAllowed('page.commissions')) && (
            <div className="nav-section-heading">{lang === 'ar' ? '🤝 الوسطاء' : '🤝 Brokers'}</div>
          )}
          {pageAllowed('page.brokers') && (
            <NavLink to="/brokers" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#14b8a6' }}>&#128101;</span>
              <span className="nav-label">{lang === 'ar' ? 'الوسطاء' : 'Brokers'}</span>
            </NavLink>
          )}
          {pageAllowed('page.commissions') && (
            <NavLink to="/commissions" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon" style={{ color: '#22c55e' }}>&#128181;</span>
              <span className="nav-label">{lang === 'ar' ? 'العمولات' : 'Commissions'}</span>
            </NavLink>
          )}

          {/* ── النظام ── */}
          {!isBroker && pageAllowed('page.settings') && (
            <div className="nav-section-heading">{lang === 'ar' ? '⚙️ النظام' : '⚙️ System'}</div>
          )}
          {pageAllowed('page.settings') && (
            <NavLink to="/settings" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
              <span className="nav-icon">&#9881;</span>
              <span className="nav-label">{t('navSettings')}</span>
            </NavLink>
          )}
        </nav>

        {/* User info + logout at bottom */}
        <div className="sidebar-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <div style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--text-primary)' }}>{user?.name}</div>
            <div style={{ fontSize: '0.68rem' }}>{user?.role}</div>
          </div>
          <button onClick={logout} title={t('logout')}
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px', color: 'var(--text-secondary)', fontSize: '0.7rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {t('logout')}
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="top-bar">
          <div className="flex items-center gap-3">
            <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={t('toggleMenu')} aria-expanded={sidebarOpen}>
              &#9776;
            </button>
            <h1 className="page-title">{getPageTitle()}</h1>
          </div>
          <div className="top-bar-actions">
            <button
              className="lang-toggle"
              onClick={toggleLang}
              title={lang === 'en' ? t('switchToArabic') : t('switchToEnglish')}
            >
              <span className="lang-toggle-icon">&#127760;</span>
              <span className="lang-toggle-label">{lang === 'en' ? 'العربية' : 'English'}</span>
            </button>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={isDark ? t('switchToLight') : t('switchToDark')}
            >
              {isDark ? '\u2600\uFE0F' : '\uD83C\uDF19'}
            </button>
          </div>
        </header>
        <main className="page-content" key={location.pathname} role="main">
          <Routes>
            <Route path="/portal" element={<BrokerPortal />} />
            <Route path="/executive" element={isBroker ? <BrokerPortal /> : (pageAllowed('page.executive') && brand.executiveEnabled !== false) ? <Executive /> : isSales ? <TodayTasks /> : pageAllowed('page.analytics') ? <Navigate to="/analytics" replace /> : <Navigate to="/leads" replace />} />
            <Route path="/" element={isBroker ? <BrokerPortal /> : (pageAllowed('page.executive') && brand.executiveEnabled !== false) ? <Executive /> : isSales ? <TodayTasks /> : pageAllowed('page.analytics') ? <Navigate to="/analytics" replace /> : <Navigate to="/leads" replace />} />
            <Route path="/today" element={isBroker ? <TodayTasks /> : pageAllowed('page.today') ? <TodayTasks /> : accessDenied} />
            <Route path="/channels" element={isBroker ? <BrokerPortal /> : (pageAllowed('page.channels') && brand.channelsEnabled !== false) ? <Channels /> : isSales ? <Leads /> : accessDenied} />
            <Route path="/leads" element={isBroker ? <Leads /> : pageAllowed('page.leads') ? <Leads /> : accessDenied} />
            <Route path="/units-deals/*" element={isBroker ? <BrokerPortal /> : pageAllowed('page.units_deals') ? <UnitsDeals /> : isSales ? <Leads /> : accessDenied} />
            <Route path="/deal-requests" element={isBroker ? <BrokerPortal /> : pageAllowed('page.deal_requests') ? <DealRequests /> : accessDenied} />
            <Route path="/analytics" element={isBroker ? <BrokerPortal /> : pageAllowed('page.analytics') ? <LeadAnalytics /> : isSales ? <TodayTasks /> : accessDenied} />
            <Route path="/analytics/member/:name" element={isBroker ? <BrokerPortal /> : pageAllowed('page.analytics') ? <MemberAnalytics /> : isSales ? <TodayTasks /> : accessDenied} />
            <Route path="/assistant" element={isBroker ? <BrokerPortal /> : pageAllowed('page.assistant') ? <Assistant /> : accessDenied} />
            <Route path="/settings" element={isBroker ? <BrokerPortal /> : pageAllowed('page.settings') ? <Settings /> : isSales ? <Leads /> : accessDenied} />
            <Route path="/broker-analytics" element={isBrokerOwner ? <BrokerAnalytics /> : isBroker ? <BrokerPortal /> : accessDenied} />
            <Route path="/broker-employees" element={isBrokerOwner ? <BrokerEmployees /> : isBroker ? <BrokerPortal /> : accessDenied} />
            <Route path="/project-media" element={pageAllowed('page.project_media') ? <ProjectMedia canManage={actionAllowed('project_media.manage')} /> : isBroker ? <ProjectMedia /> : accessDenied} />
            <Route path="/brokers" element={pageAllowed('page.brokers') ? <BrokerMonitor /> : accessDenied} />
            <Route path="/commissions" element={pageAllowed('page.commissions') ? <AdminCommissions /> : accessDenied} />
            <Route path="/stale-leads" element={pageAllowed('page.stale_leads') ? <StaleLeads /> : accessDenied} />
            <Route path="/campaign-composer" element={pageAllowed('page.campaigns') && brand.campaignsEnabled !== false ? <CampaignComposer /> : accessDenied} />
            <Route path="/maintenance" element={pageAllowed('page.maintenance') ? <Maintenance /> : accessDenied} />
            <Route path="/tasks" element={isBroker ? <BrokerPortal /> : pageAllowed('page.tasks') ? <Tasks /> : accessDenied} />
            <Route path="/closure-tasks" element={isBroker ? <BrokerPortal /> : pageAllowed('page.closure_tasks') ? <ClosureTasks /> : accessDenied} />
            <Route path="/broker-commissions" element={canBrokerFinancials ? <BrokerCommissions /> : isBroker ? <BrokerPortal /> : accessDenied} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default App;
